import React, {
  createContext, useContext, useMemo, useEffect
} from 'react';
import {
  node, any, string, func, number
} from 'prop-types';
import {
  useDataModel, extend, alias, string as stringType, arrayOf as arrayOfType
} from '@thd-nucleus/data-sources';
import { dataModel } from '../components/dataModel';
import { transformItemDataToPromoModel } from '../utils/promo-model-utils';
import { sortItemToTopOfList } from '../utils/promo-utils';

export const PromoModelContext = createContext();
export const usePromoModel = () => useContext(PromoModelContext);

export const PromoModelProvider = ({
  anchorItem, storeId, storeZip, setIsBatchLoading, productDataModel, currentPromoTier, children
}) => {

  // We are limited to calls with 16 items and need to ensure that anchor is part of those 16
  let itemIds = (anchorItem.pricing.promotion.itemList || []).slice();
  itemIds = sortItemToTopOfList(itemIds, anchorItem.itemId).slice(0, 16);
  const mergedDataModel = extend({}, productDataModel, dataModel.discountedMultipleItemId);
  const dataModelQuery = {
    discountedMultipleItemId: alias('products').params({
      itemIds: arrayOfType(stringType().isRequired()).isRequired(),
      dataSource: stringType()
    }).shape(mergedDataModel)
  };
  const { data, loading } = useDataModel('discountedMultipleItemId', {
    variables: {
      itemIds,
      storeId,
      dataSource: 'discounted-items',
    },
    dataModel: dataModelQuery,
    ssr: false,
  });

  useEffect(() => setIsBatchLoading(loading), [loading, setIsBatchLoading]);

  const promoModel = useMemo(() => transformItemDataToPromoModel(data?.products, anchorItem), [data, anchorItem]);

  const rewardTiers = promoModel.rewardTiers;

  const contextValue = {
    storeId,
    storeZip,
    anchorItem,
    anchorItemId: anchorItem.itemId,
    anchorItemImage: promoModel.anchorItemImage,
    allFulfillableProductsAndAnchor: promoModel.allFulfillableProductsAndAnchor,
    promoId: promoModel.promoId,
    isBogo: promoModel.isBogo,
    isMsb: promoModel.isMsb,
    isBmsm: promoModel.isBmsm,
    analyticsComponent: promoModel.analyticsComponent,
    analyticsSharedSection: promoModel.analyticsSharedSection,
    displayableItemIds: promoModel.displayableItemIds,
    hasProducts: promoModel.hasProducts,
    itemCount: promoModel.itemCount,
    singleItemIdToDisplay: promoModel.singleItemIdToDisplay,
    isAnchorFulfillable: promoModel.isAnchorFulfillable,
    experienceTag: promoModel.experienceTag,
    subExperience: promoModel.subExperience,
    bogoMaxQuantity: 1, // TODO: link to dynamic data when available
    minPurchaseAmount: rewardTiers[currentPromoTier].minPurchaseAmount,
    minPurchaseQuantity: rewardTiers[currentPromoTier].minPurchaseQuantity,
    rewardAmountPerItem: rewardTiers[currentPromoTier].rewardAmountPerItem,
    rewardAmountPerOrder: rewardTiers[currentPromoTier].rewardAmountPerOrder,
    rewardPercent: rewardTiers[currentPromoTier].rewardPercent,
    rewardTiers,
  };

  return (
    <PromoModelContext.Provider value={contextValue}>
      { children }
    </PromoModelContext.Provider>
  );
};

PromoModelProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  anchorItem: any,
  storeId: string.isRequired,
  storeZip: string,
  setIsBatchLoading: func.isRequired,
  currentPromoTier: number,
  children: node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  productDataModel: any,
};

PromoModelProvider.defaultProps = {
  anchorItem: {},
  storeZip: '',
  currentPromoTier: 0,
  productDataModel: {},
};
